export type AuthQuery = Record<string, string | string[] | undefined>;

export type BuildLoginUrlExtra = {
  task?: string;
  comparisonTaskId?: string;
  goal?: string;
  awsExternalId?: string;
  error?: string;
};

const EMAIL_CALLBACK_STORAGE_KEY = 'email_magiclink_callback';
const EMAIL_TASK_STORAGE_KEY = 'email_magiclink_task';
const EMAIL_GOAL_STORAGE_KEY = 'email_magiclink_goal';
const EMAIL_AWS_STORAGE_KEY = 'email_magiclink_aws';

export function resolveCallbackUrl(query?: AuthQuery | null): string {
  const raw = query?.callbackUrl;
  if (typeof raw === 'string' && raw) return raw;
  if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0]) {
    return raw[0];
  }
  return '/';
}

export function resolveQueryParam(
  query: AuthQuery | null | undefined,
  key: string,
): string | null {
  const raw = query?.[key];
  if (typeof raw === 'string' && raw) return raw;
  if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0]) {
    return raw[0];
  }
  return null;
}

export function persistEmailLoginContext(
  callbackUrl: string,
  task?: string | null,
  goal?: string | null,
  awsExternalId?: string | null,
) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(EMAIL_CALLBACK_STORAGE_KEY, callbackUrl || '/');
  if (task) {
    localStorage.setItem(EMAIL_TASK_STORAGE_KEY, task);
  } else {
    localStorage.removeItem(EMAIL_TASK_STORAGE_KEY);
  }
  if (goal) {
    localStorage.setItem(EMAIL_GOAL_STORAGE_KEY, goal);
  } else {
    localStorage.removeItem(EMAIL_GOAL_STORAGE_KEY);
  }
  if (awsExternalId) {
    localStorage.setItem(EMAIL_AWS_STORAGE_KEY, awsExternalId);
  } else {
    localStorage.removeItem(EMAIL_AWS_STORAGE_KEY);
  }
}

export function readStoredEmailCallbackUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(EMAIL_CALLBACK_STORAGE_KEY);
}

export function readStoredEmailTask(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(EMAIL_TASK_STORAGE_KEY);
}

export function readStoredEmailGoal(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(EMAIL_GOAL_STORAGE_KEY);
}

export function readStoredEmailAwsExternalId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(EMAIL_AWS_STORAGE_KEY);
}

function withAuthParams(
  path: string,
  callbackUrl: string = '/',
  extra?: BuildLoginUrlExtra,
): string {
  const params = new URLSearchParams();
  if (extra?.error) params.set('error', extra.error);
  if (extra?.task) params.set('task', extra.task);
  if (extra?.comparisonTaskId) {
    params.set('comparisonTaskId', extra.comparisonTaskId);
  }
  if (extra?.goal) params.set('goal', extra.goal);
  if (extra?.awsExternalId) params.set('awsExternalId', extra.awsExternalId);
  params.set('callbackUrl', callbackUrl || '/');
  return `${path}?${params.toString()}`;
}

export function buildLoginUrl(
  callbackUrl: string = '/',
  extra?: BuildLoginUrlExtra,
): string {
  return withAuthParams('/auth/login', callbackUrl, extra);
}

export function buildVerifyEmailUrl(
  callbackUrl: string = '/',
  extra?: Pick<BuildLoginUrlExtra, 'task' | 'comparisonTaskId' | 'goal' | 'awsExternalId'>,
): string {
  return withAuthParams('/auth/verify-email', callbackUrl, extra);
}
